import type { EventStatus } from './api/endpoints';

/** Geld: ganze Cent als Euro-Text (nie Fließkomma rechnen, nur anzeigen). */
export function euro(cents: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

/** Zeiten sind in UTC gespeichert, angezeigt wird in Europe/Berlin. */
export function dateTime(iso: string | null): string {
  if (iso === null) return '–';
  return new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'Europe/Berlin',
  }).format(new Date(iso));
}

/** `<input type="datetime-local">` liefert Ortszeit ohne Zone: als UTC-Zeitpunkt (ISO) an die API. */
export function localInputToIso(value: string): string | null {
  if (value === '') return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

/** UTC-Zeitpunkt in die Ortszeit für ein `datetime-local`-Feld. */
export function isoToLocalInput(iso: string | null): string {
  if (iso === null) return '';
  const date = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${String(date.getFullYear())}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export const STATUS_LABELS: Record<EventStatus, string> = {
  draft: 'Entwurf',
  published: 'Veröffentlicht',
  registration_open: 'Anmeldung offen',
  intake: 'Annahme',
  selling: 'Verkauf',
  returns: 'Rückgabe und Abbau',
  closed: 'Geschlossen',
  settled: 'Abgerechnet',
  archived: 'Archiviert',
};

/**
 * Euro-Eingabe („12,50“, „-3“, „+0,5“) in ganze Cent, ohne Fließkomma. Ungültiges (leer, mehr als zwei Nachkommastellen,
 * Buchstaben, zu groß) ergibt `null`.
 */
export function parseCents(input: string): number | null {
  const match = /^([+-])?(\d{1,7})(?:[.,](\d{1,2}))?$/.exec(input.trim());
  if (match === null) return null;
  const cents = Number(match[2]) * 100 + Number((match[3] ?? '').padEnd(2, '0') || '0');
  return match[1] === '-' ? -cents : cents;
}

/** Anzahl mit Tausenderpunkt („1.996“), nur zur Anzeige. */
export function count(value: number): string {
  return new Intl.NumberFormat('de-DE').format(value);
}

/** Prozentsatz aus Basispunkten (1600 → „16 %“, 250 → „2,5 %“), nur zur Anzeige. */
export function percentOf(basisPoints: number): string {
  return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: 2 }).format(basisPoints / 100)} %`;
}

/** Uhrzeit in Berlin („14:32“), z. B. für „Stand 14:32“. */
export function clockTime(iso: string): string {
  return new Intl.DateTimeFormat('de-DE', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  }).format(new Date(iso));
}

/**
 * Termin für die Hero-Karte: am selben Tag „Sa., 17.10.2026 · 09:00 bis 13:00 Uhr“, sonst Anfang bis Ende.
 * Ohne Termin „Termin noch offen“.
 */
export function eventWhen(startsAt: string | null, endsAt: string | null): string {
  if (startsAt === null || endsAt === null) {
    if (startsAt === null && endsAt === null) return 'Termin noch offen';
    return endsAt === null ? `ab ${dateTime(startsAt)}` : `bis ${dateTime(endsAt)}`;
  }
  const day = new Intl.DateTimeFormat('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Berlin',
  });
  if (day.format(new Date(startsAt)) !== day.format(new Date(endsAt))) {
    return `${dateTime(startsAt)} bis ${dateTime(endsAt)}`;
  }
  return `${day.format(new Date(startsAt))} · ${clockTime(startsAt)} bis ${clockTime(endsAt)} Uhr`;
}

/** Zeitraum für den Kopf der Basar-Seite; ohne Termin kein „– bis –“. */
export function period(startsAt: string | null, endsAt: string | null): string {
  if (startsAt === null && endsAt === null) return 'Termin noch offen';
  if (endsAt === null) return `ab ${dateTime(startsAt)}`;
  if (startsAt === null) return `bis ${dateTime(endsAt)}`;
  return `${dateTime(startsAt)} bis ${dateTime(endsAt)}`;
}
